import { Validator } from 'jsonschema';
import * as Sentry from '@sentry/browser';


type DeviceMetadata = Record<string, string>;

type MetadataType = 'string'

// Defensive boundary check, intentionally independent of the API's
// authoritative validation in fox-service-api/src/utils/deviceMetadata.ts.
// This reads stored client config and degrades gracefully (drops invalid
// entries rather than crashing the device screen), so it may be looser than
// and need not match the server's canonical rules.
interface DeviceMetadataPreferences {
  order: number
  type: MetadataType
  render: Record<string, never> // reserved for future use
  input: Record<string, never> // reserved for future use
}

type DeviceMetadataPreferencesMap = { [key: string]: DeviceMetadataPreferences };

type RenderOptions = DeviceMetadataPreferences['render'];

const metadataSchema = {
  type: 'object',
  properties: {
    order: { type: 'number' },
    type: { type: 'string', enum: ['string'] },
    render: {
      type: 'object',
      properties: {},
      additionalProperties: false
    },
    input: {
      type: 'object',
      properties: {},
      additionalProperties: false
    },
  },
  required: ['order', 'type'],
  additionalProperties: false
};


const jsonToMetadataPreferences = (metadata: any): DeviceMetadataPreferencesMap => {
  const cleanedMetadata: DeviceMetadataPreferencesMap = {};
  const validator = new Validator();

  for (const [key, value] of Object.entries(metadata || {})) {
    const validationResult = validator.validate(value, metadataSchema);
    if (validationResult.valid) {
      const approved = value as DeviceMetadataPreferences;
      // render and input are optional on incoming data but required by the
      // type, so they are defaulted to an empty object
      const normalized: DeviceMetadataPreferences = {
        ...approved,
        render: approved.render ?? {},
        input: approved.input ?? {}
      };
      cleanedMetadata[key] = normalized;
    } else {
      // eslint-disable-next-line no-console
      console.warn(`Invalid metadata for key ${key}:`, validationResult.errors);
      Sentry.withScope(scope => {
        scope.setLevel('warning');
        scope.setExtra('metadataKey', key);
        scope.setExtra('validationErrors', validationResult.errors.map(e => e.toString()));
        Sentry.captureMessage(`Invalid device client metadata for key "${key}"`);
      });
    }
  }

  return cleanedMetadata;
};


export { jsonToMetadataPreferences };
export type { DeviceMetadataPreferences, DeviceMetadataPreferencesMap, RenderOptions, MetadataType };
